import React, { PropsWithChildren, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import ErrorPage from 'next/error';
import { Layout } from '@raisin/cp-layout';
import BankPageSelector from 'components/BankPageSelector';
import Banks from 'components/Banks';
import { BankPageContentProps } from 'types';
import { getLayoutData, getPathParts, isBankOfferIdValid } from './utils';

export const BankPagesProvider: React.FC<
  PropsWithChildren<Pick<BankPageContentProps, 'bankPage' | 'layout' | 'locale' | 'messages'>>
> = ({ bankPage, layout, locale, messages, children }) => {
  const {
    titleId,
    navigation_settings: bankNavigationSettings,
    seo_settings: bankSeoSettings,
  } = bankPage;

  const { bankSeoSettings: bankSeoSettingsData, layout: layoutData } = getLayoutData({
    bankNavigationSettings,
    bankSeoSettings,
    layout,
  });

  return (
    <IntlProvider locale={locale}>
      <Layout
        {...{
          layout: layoutData,
          pageTitle: (bankPage.bank_details?.name || messages[titleId]) as string,
          bankSeoSettings: bankSeoSettingsData,
          footerGap: true,
        }}
        isPortal={false}
        isAuthRequired={false}
        isBypassAuthContext={false}
        pageMessages={messages}
      >
        {children}
      </Layout>
    </IntlProvider>
  );
};

export const BankPageContent: React.FC<BankPageContentProps> = ({
  bankPage,
  banks,
  asPath,
  ...props
}) => {
  const pathParts = getPathParts(asPath);
  const providerProps = useMemo(
    () => ({
      ...props,
      bankPage,
    }),
    [props, bankPage],
  );

  if (pathParts.length === 3 && bankPage.bank_offer_id && !isBankOfferIdValid(bankPage)) {
    return <ErrorPage statusCode={404} />;
  }

  if (bankPage.bank_id && bankPage.bank_details) {
    return (
      <BankPagesProvider {...providerProps}>
        <BankPageSelector
          bank={bankPage.bank_details}
          offers={bankPage.bank_offers}
          bankOfferId={bankPage.bank_offer_id}
          highestInterestRate={bankPage.highestInterestRate}
        />
      </BankPagesProvider>
    );
  }

  if (banks) {
    return (
      <BankPagesProvider {...providerProps}>
        <Banks banksList={banks} />
      </BankPagesProvider>
    );
  }

  return <ErrorPage statusCode={404} />;
};
